import React from 'react';
import { Plus, Search, Trash2, X } from 'lucide-react';
import Modal from '../Modal';
import SearchableSelect from '../SearchableSelect';
import {
  AI_INPUT_ALL_FIELDS,
  changeAiActionSourceFields,
  getAiActionSourceFields
} from '../../utils/ruleActions';
import { buildTransitionStatusOptions } from '../../utils/statusOptions';
import {
  ACTION_TYPE_OPTIONS,
  ASSIGNMENT_STRATEGY_OPTIONS
} from './ruleEditorOptions';

const PRIORITY_OPTIONS = [
  { value: 'Lowest', label: '最低' },
  { value: 'Low', label: '低' },
  { value: 'Medium', label: '中' },
  { value: 'High', label: '高' },
  { value: 'Highest', label: '最高' }
];

const HTTPS_METHOD_OPTIONS = ['POST', 'PUT', 'PATCH', 'GET', 'DELETE'].map(
  method => ({ value: method, label: method })
);

const COMMENT_TYPE_OPTIONS = [
  { value: 'custom', label: '自定义评论' },
  { value: 'ai', label: 'AI 生成评论' },
  { value: 'condition_result', label: '使用 AI 条件结果' }
];

const FormField = ({ label, className = '', children }) => (
  <div className={`form-group ${className}`.trim()}>
    <label className="form-label">{label}</label>
    {children}
  </div>
);

const RuleActionModal = ({
  isOpen,
  isEditing,
  action,
  fields,
  aiConfigs,
  aiConfigsError = '',
  aiConditionOptions = [],
  statuses,
  actionFieldOptions,
  aiInputFieldOptions = [{
    value: AI_INPUT_ALL_FIELDS,
    label: '全部字段'
  }],
  fieldInsertOptions,
  selectedAssigneeIds,
  selectedAssignees,
  filteredPersons,
  assigneeSearch,
  bodyJsonError,
  bodyTextareaRef,
  isComplete,
  onChange,
  onSearchChange,
  onTypeChange,
  onToggleAssignee,
  onClearAssignees,
  onAddHeader,
  onUpdateHeader,
  onRemoveHeader,
  onFormatBody,
  onInsertBodyField,
  onSubmit,
  onClose
}) => {
  const updateAction = (patch) => {
    onChange(previous => ({ ...previous, ...patch }));
  };

  const aiOptions = aiConfigsError
    ? [{
        value: '',
        label: aiConfigsError,
        disabled: true
      }]
    : aiConfigs.length === 0
      ? [{
          value: '',
          label: '暂无启用的 AI，请先配置',
          disabled: true
        }]
      : aiConfigs.map(ai => ({
          value: ai.provider,
          label: ai.label || `${ai.provider} (${ai.model})`
        }));
  const fieldOptions = fields.map(field => ({
    value: field.field_id,
    label: field.display_name
  }));
  const aiSourceFields = getAiActionSourceFields(action);

  const renderAiSourceField = () => (
    <FormField label="AI 输入字段">
      <SearchableSelect
        value={aiSourceFields}
        options={aiInputFieldOptions}
        multiple={true}
        onChange={(nextFields) => updateAction({
          source_fields: changeAiActionSourceFields(
            aiSourceFields,
            nextFields
          )
        })}
        placeholder="选择一个或多个输入字段"
      />
    </FormField>
  );

  return (
    <Modal
      isOpen={isOpen}
      title={isEditing ? '编辑执行动作' : '添加执行动作'}
      className="rule-action-modal"
      size="xlarge"
      onClose={onClose}
      buttons={[
        { label: '取消', onClick: onClose, variant: 'default' },
        {
          label: isEditing ? '更新动作' : '添加动作',
          onClick: onSubmit,
          variant: 'primary',
          disabled: !isComplete
        }
      ]}
    >
      <div className={`rule-action-form action-${action.type}`}>
        <FormField label="动作类型" className="rule-action-type-field">
          <SearchableSelect
            value={action.type}
            options={ACTION_TYPE_OPTIONS}
            onChange={onTypeChange}
            placeholder="选择动作类型"
            searchable={false}
          />
        </FormField>

        {action.type === 'assign' && (
          <div className="rule-action-parameter-section assignment-action">
            <FormField label="分配策略">
              <SearchableSelect
                value={action.assign_rule}
                options={ASSIGNMENT_STRATEGY_OPTIONS}
                onChange={(assignRule) => updateAction({
                  assign_rule: assignRule
                })}
                placeholder="选择分配策略"
                searchable={false}
              />
            </FormField>

            <div className="assignee-picker">
              <div className="assignee-picker-header">
                <div>
                  <label className="form-label">选择人员</label>
                  <span>{selectedAssigneeIds.length} 人已选</span>
                </div>
                {selectedAssigneeIds.length > 0 && (
                  <button
                    type="button"
                    className="assignee-clear-btn"
                    onClick={onClearAssignees}
                  >
                    清空
                  </button>
                )}
              </div>

              <div className="assignee-search">
                <Search size={15} aria-hidden="true" />
                <input
                  type="search"
                  value={assigneeSearch}
                  onChange={(event) => onSearchChange(event.target.value)}
                  placeholder="搜索姓名、邮箱或账号 ID"
                />
              </div>

              <div className="assignee-list">
                {filteredPersons.length === 0 ? (
                  <div className="assignee-empty">没有匹配的人员</div>
                ) : (
                  filteredPersons.map(person => {
                    const selected = selectedAssigneeIds.includes(
                      person.account_id
                    );
                    return (
                      <label
                        key={person.id || person.account_id}
                        className={`assignee-option ${selected ? 'selected' : ''}`}
                      >
                        <input
                          type="checkbox"
                          checked={selected}
                          onChange={(event) => onToggleAssignee(
                            person.account_id,
                            event.target.checked
                          )}
                        />
                        <span className="assignee-avatar">
                          {(person.name || person.account_id || '?')
                            .slice(0, 1)
                            .toUpperCase()}
                        </span>
                        <span className="assignee-info">
                          <strong>{person.name}</strong>
                          <small>{person.email || person.account_id}</small>
                        </span>
                      </label>
                    );
                  })
                )}
              </div>

              {selectedAssignees.length > 0 && (
                <div className="selected-assignees">
                  <span>已选择</span>
                  {selectedAssignees.map(person => (
                    <span
                      key={person.account_id}
                      className="selected-assignee-tag"
                    >
                      {person.name}
                      <button
                        type="button"
                        onClick={() => onToggleAssignee(
                          person.account_id,
                          false
                        )}
                        aria-label={`移除 ${person.name}`}
                      >
                        <X size={13} aria-hidden="true" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {action.type === 'update_priority' && (
          <div className="rule-action-parameter-section compact-action">
            <FormField label="优先级">
              <SearchableSelect
                value={action.target}
                options={PRIORITY_OPTIONS}
                onChange={(target) => updateAction({ target })}
                placeholder="选择优先级"
                searchable={false}
              />
            </FormField>
          </div>
        )}

        {action.type === 'transition' && (
          <div className="rule-action-parameter-section compact-action">
            <FormField label="目标状态">
              <SearchableSelect
                value={action.target}
                options={buildTransitionStatusOptions(
                  statuses,
                  action.target
                )}
                onChange={(target) => updateAction({ target })}
                placeholder={statuses.length > 0
                  ? '选择目标状态'
                  : '请先在字段管理中同步工单状态字段'}
              />
            </FormField>
          </div>
        )}

        {action.type === 'set_field' && (
          <div className="rule-action-parameter-section set-field-action">
            <FormField label="选择字段">
              <SearchableSelect
                value={action.field || ''}
                options={fieldOptions}
                onChange={(field) => updateAction({ field, value: '' })}
                placeholder="选择字段"
              />
            </FormField>
            <FormField label="字段值">
              {actionFieldOptions.length > 0 ? (
                <SearchableSelect
                  value={action.value || ''}
                  options={actionFieldOptions.map(option => ({
                    value: option.value,
                    label: option.label || option.value
                  }))}
                  onChange={(value) => updateAction({ value })}
                  placeholder="选择字段值"
                  emptyText="没有匹配的字段选项"
                />
              ) : (
                <input
                  type="text"
                  value={action.value || ''}
                  onChange={(event) => updateAction({
                    value: event.target.value
                  })}
                  className="form-input"
                  placeholder="输入字段值"
                />
              )}
            </FormField>
          </div>
        )}

        {action.type === 'salesforce_customer_sync' && (
          <div className="rule-action-parameter-section salesforce-sync-action">
            <FormField label="Handle 来源字段">
              <SearchableSelect
                value={action.handle_field || ''}
                options={fieldOptions}
                onChange={(handleField) => updateAction({
                  handle_field: handleField
                })}
                placeholder="选择 Jira Handle 字段"
              />
            </FormField>
            {[
              ['customer_names', 'Salesforce 客户名称'],
              ['stock_monthly_gmv', '存量月 GMV（美金）'],
              ['potential_monthly_gmv', '潜在月均 GMV（美金）'],
              ['total_monthly_gmv', '总月 GMV（美金）'],
              ['customer_new_year', '客户新增年份']
            ].map(([key, label]) => (
              <FormField label={key === 'customer_names' ? label : `${label}（可选）`} key={key}>
                <SearchableSelect
                  value={action.target_fields?.[key] || ''}
                  options={fieldOptions}
                  onChange={(value) => updateAction({
                    target_fields: {
                      ...action.target_fields,
                      [key]: value
                    }
                  })}
                  placeholder="选择回填字段"
                />
              </FormField>
            ))}
            <label className="form-help rule-action-wide-field"><input type="checkbox" checked={action.clear_on_no_match !== false} onChange={event => updateAction({ clear_on_no_match: event.target.checked })} />确认未匹配到客户时清空已配置的回填字段</label>
            <small className="form-help rule-action-wide-field">
              复用商家管理中的 Salesforce 配置，通过 Handle 获取标准客户名称；未启用时沿用本地快照。客户名称必填，其余回填字段可选。查询失败或部分匹配不覆盖字段；确认无匹配时沿用规则的清空设置。GMV 数据不完整时保留原值。
            </small>
          </div>
        )}

        {action.type === 'ai_set_field' && (
          <div className="rule-action-parameter-section ai-set-field-action">
            {renderAiSourceField()}
            <FormField label="目标字段">
              <SearchableSelect
                value={action.field || ''}
                options={fieldOptions}
                onChange={(field) => updateAction({ field })}
                placeholder="选择目标字段"
              />
            </FormField>
            <FormField label="选择 AI">
              <SearchableSelect
                value={action.ai_provider || ''}
                options={aiOptions}
                onChange={(aiProvider) => updateAction({
                  ai_provider: aiProvider
                })}
                placeholder="选择 AI 提供商"
              />
            </FormField>
            <FormField label="AI 提示词" className="rule-action-wide-field">
              <textarea
                value={action.ai_prompt || ''}
                onChange={(event) => updateAction({
                  ai_prompt: event.target.value
                })}
                className="form-input"
                placeholder="告诉 AI 如何生成目标字段值。如果目标字段是选项字段，请要求 AI 只返回一个可选值。"
                rows={5}
              />
              <small className="form-help">
                目标字段是单选或级联字段时，请要求 AI 只返回 Jira 字段可选值。
              </small>
            </FormField>
          </div>
        )}

        {action.type === 'add_comment' && (
          <div className="rule-action-parameter-section comment-action">
            <FormField label="评论类型">
              <SearchableSelect
                value={action.comment_type || 'custom'}
                options={COMMENT_TYPE_OPTIONS}
                onChange={(commentType) => updateAction({
                  comment_type: commentType
                })}
                placeholder="选择评论类型"
                searchable={false}
              />
            </FormField>

            {action.comment_type === 'condition_result' ? (
              <FormField label="引用 AI 条件" className="rule-action-wide-field">
                <SearchableSelect value={action.ai_condition_id || ''}
                  options={aiConditionOptions}
                  onChange={ai_condition_id => updateAction({ ai_condition_id })}
                  placeholder="选择判断并生成评论的 AI 条件" />
                <small className="form-help">复用本次执行中该条件的评论，不再调用 AI。源条件未通过、失败或被短路跳过时不发表评论。</small>
                {action.ai_condition_id && !aiConditionOptions.some(option => option.value === action.ai_condition_id)
                  && <small className="form-help">原引用条件已删除或改变模式，请重新选择。</small>}
              </FormField>
            ) : action.comment_type === 'custom' ? (
              <FormField label="评论内容" className="rule-action-wide-field">
                <textarea
                  value={action.comment || ''}
                  onChange={(event) => updateAction({
                    comment: event.target.value
                  })}
                  className="form-input"
                  placeholder="输入评论内容，支持多行"
                  rows={5}
                />
              </FormField>
            ) : (
              <>
                {renderAiSourceField()}
                <FormField label="选择 AI">
                  <SearchableSelect
                    value={action.ai_provider || ''}
                    options={aiOptions}
                    onChange={(aiProvider) => updateAction({
                      ai_provider: aiProvider
                    })}
                    placeholder="选择 AI 提供商"
                  />
                </FormField>
                <FormField
                  label="AI 提示词"
                  className="rule-action-wide-field"
                >
                  <textarea
                    value={action.ai_prompt || ''}
                    onChange={(event) => updateAction({
                      ai_prompt: event.target.value
                    })}
                    className="form-input"
                    placeholder="输入 AI 生成评论的提示词，支持多行"
                    rows={5}
                  />
                </FormField>
              </>
            )}
          </div>
        )}

        {action.type === 'send_https_request' && (
          <div className="rule-action-parameter-section https-request-action">
            <div className="https-request-target">
              <FormField label="请求方法">
                <SearchableSelect
                  value={action.method || 'POST'}
                  options={HTTPS_METHOD_OPTIONS}
                  onChange={(method) => updateAction({ method })}
                  placeholder="方法"
                  searchable={false}
                />
              </FormField>
              <FormField label="HTTPS URL">
                <input
                  type="url"
                  value={action.url || ''}
                  onChange={(event) => updateAction({
                    url: event.target.value
                  })}
                  className="form-input"
                  placeholder="https://example.com/webhook"
                />
              </FormField>
            </div>

            <div className="https-request-config">
              <section className="https-request-panel">
                <div className="https-request-panel-header">
                  <label className="form-label">Header</label>
                  <button
                    type="button"
                    className="btn btn-sm btn-secondary"
                    onClick={onAddHeader}
                  >
                    <Plus size={14} aria-hidden="true" />
                    新增 Header
                  </button>
                </div>
                <div className="https-header-labels">
                  <span>名称</span>
                  <span>值</span>
                  <span aria-hidden="true" />
                </div>
                {(action.headers || [{ name: '', value: '' }]).map(
                  (header, index) => (
                    <div className="https-header-row" key={index}>
                      <input
                        type="text"
                        value={header.name || ''}
                        onChange={(event) => onUpdateHeader(
                          index,
                          'name',
                          event.target.value
                        )}
                        className="form-input"
                        placeholder="Content-Type"
                      />
                      <input
                        type="text"
                        value={header.value || ''}
                        onChange={(event) => onUpdateHeader(
                          index,
                          'value',
                          event.target.value
                        )}
                        className="form-input"
                        placeholder="application/json"
                      />
                      <button
                        type="button"
                        className="rule-action-icon-button is-danger"
                        onClick={() => onRemoveHeader(index)}
                        disabled={(action.headers || []).length <= 1}
                        aria-label={`删除 Header ${index + 1}`}
                        title="删除 Header"
                      >
                        <Trash2 size={15} aria-hidden="true" />
                      </button>
                    </div>
                  )
                )}
              </section>

              <section className={`https-request-panel ${bodyJsonError ? 'has-error' : ''}`}>
                <div className="https-request-panel-header">
                  <label className="form-label">Body</label>
                  <div className="https-body-tools">
                    <button
                      type="button"
                      className="btn btn-sm btn-secondary"
                      onClick={onFormatBody}
                      disabled={!String(action.body || '').trim()
                        || Boolean(bodyJsonError)}
                    >
                      格式化 JSON
                    </button>
                    <SearchableSelect
                      value=""
                      options={fieldInsertOptions}
                      onChange={onInsertBodyField}
                      placeholder="插入字段"
                    />
                  </div>
                </div>
                <textarea
                  ref={bodyTextareaRef}
                  value={action.body || ''}
                  onChange={(event) => updateAction({
                    body: event.target.value
                  })}
                  className="form-input https-body-input"
                  placeholder={'{"issue":"{{issue.key}}"}'}
                  rows={10}
                />
                <div
                  className={`https-json-status ${bodyJsonError ? 'is-error' : 'is-valid'}`}
                >
                  {bodyJsonError
                    ? `JSON 格式错误: ${bodyJsonError}`
                    : String(action.body || '').trim()
                      ? 'JSON 格式有效'
                      : 'Body 可为空；填写后会校验 JSON 格式'}
                </div>
              </section>
            </div>
          </div>
        )}

        {action.type === 'noop' && (
          <div className="rule-action-parameter-section noop-action">
            <strong>不执行任何操作</strong>
            <span>仅记录规则匹配结果，不执行 Jira 操作。</span>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default RuleActionModal;
